import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal } from '@components/molecules/Modal';
import { Input } from '@components/atoms/Input';
import { Checkbox } from '@components/atoms/Checkbox';
import { Task } from '@models/task.model';
import { ChevronRight, ChevronDown, Search } from 'lucide-react';

interface SelectTasksModalProps {
  isOpen: boolean;
  onClose: () => void;
  tasks: Task[];
  onConfirm: (selectedTaskIds: string[]) => void;
  title?: string;
  confirmText?: string;
  initialSelection?: string[];
}

interface TaskWithChildren extends Task {
  children: TaskWithChildren[];
}

export const SelectTasksModal: React.FC<SelectTasksModalProps> = ({
  isOpen,
  onClose,
  tasks,
  onConfirm,
  title = 'Select Tasks',
  confirmText = 'Confirm Selection',
  initialSelection = [],
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(initialSelection));
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Track previous isOpen value to detect modal open events
  const prevIsOpenRef = useRef(false);

  useEffect(() => {
    // Only reset state when modal OPENS (transitions from closed to open)
    if (isOpen && !prevIsOpenRef.current) {
      setSelectedIds(new Set(initialSelection));
      const parentIds = tasks
        .filter((t) => !t.parentTaskId)
        .map((t) => t.taskId!)
        .filter((id): id is string => !!id);
      setExpandedIds(new Set(parentIds));
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, initialSelection, tasks]);

  const taskById = useMemo(() => {
    const map = new Map<string, Task>();
    tasks.forEach((task) => {
      if (task.taskId) map.set(task.taskId, task);
    });
    return map;
  }, [tasks]);

  const taskTree = useMemo(() => {
    const taskMap = new Map<string, TaskWithChildren>();
    const roots: TaskWithChildren[] = [];

    tasks.forEach((task) => {
      taskMap.set(task.taskId!, { ...task, children: [] });
    });

    tasks.forEach((task) => {
      const taskWithChildren = taskMap.get(task.taskId!);
      if (taskWithChildren) {
        if (task.parentTaskId && taskMap.has(task.parentTaskId)) {
          taskMap.get(task.parentTaskId)!.children.push(taskWithChildren);
        } else {
          roots.push(taskWithChildren);
        }
      }
    });

    return roots;
  }, [tasks]);

  const filteredTaskTree = useMemo(() => {
    if (!searchQuery) return taskTree;

    const lowerQuery = searchQuery.toLowerCase();

    const filterTask = (task: TaskWithChildren): TaskWithChildren | null => {
      const matchesSearch = task.name.toLowerCase().includes(lowerQuery);
      const filteredChildren = task.children
        .map(filterTask)
        .filter((t): t is TaskWithChildren => t !== null);

      if (matchesSearch || filteredChildren.length > 0) {
        return { ...task, children: filteredChildren };
      }
      return null;
    };

    return taskTree
      .map(filterTask)
      .filter((t): t is TaskWithChildren => t !== null);
  }, [taskTree, searchQuery]);

  const getChildTaskIds = (task: TaskWithChildren): string[] => {
    const ids: string[] = [];
    const collectIds = (t: TaskWithChildren) => {
      ids.push(t.taskId!);
      t.children.forEach(collectIds);
    };
    task.children.forEach(collectIds);
    return ids;
  };

  // Walks up parentTaskId. Selecting a subtask without its parent would import an
  // orphan, so the ancestors come along.
  const getAncestorIds = (taskId: string): string[] => {
    const ancestors: string[] = [];
    let currentId = taskById.get(taskId)?.parentTaskId;

    while (currentId && taskById.has(currentId) && !ancestors.includes(currentId)) {
      ancestors.push(currentId);
      currentId = taskById.get(currentId)?.parentTaskId;
    }

    return ancestors;
  };

  const handleTaskToggle = (task: TaskWithChildren) => {
    const newSelected = new Set(selectedIds);
    // Only a fully checked task clears. Ticking a subtask now also ticks its ancestors,
    // which leaves the parent indeterminate — clicking it there must complete the
    // selection, not wipe the parent and every sibling along with it.
    const childIds = getChildTaskIds(task);
    const isSelected =
      selectedIds.has(task.taskId!) && childIds.every((id) => selectedIds.has(id));

    if (isSelected) {
      newSelected.delete(task.taskId!);
      childIds.forEach((id) => newSelected.delete(id));
    } else {
      newSelected.add(task.taskId!);
      childIds.forEach((id) => newSelected.add(id));
      getAncestorIds(task.taskId!).forEach((id) => newSelected.add(id));
    }

    setSelectedIds(newSelected);
  };

  const handleExpandToggle = (taskId: string) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId);
    } else {
      newExpanded.add(taskId);
    }
    setExpandedIds(newExpanded);
  };

  const handleSelectAll = () => {
    const allIds = tasks.map((t) => t.taskId!).filter((id): id is string => !!id);
    setSelectedIds(new Set(allIds));
  };

  const handleDeselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleConfirm = () => {
    // Emit in the order shown on screen, not Set insertion order (which is the order the
    // user happened to tick the boxes). The full tree is walked, not the filtered one, so
    // an active search never drops a selection.
    const orderedIds: string[] = [];
    const collect = (nodes: TaskWithChildren[]) => {
      nodes.forEach((node) => {
        if (selectedIds.has(node.taskId!)) orderedIds.push(node.taskId!);
        collect(node.children);
      });
    };
    collect(taskTree);

    // Anything selected that is not in the tree (e.g. an initialSelection id whose task
    // was not passed in) still gets through.
    const covered = new Set(orderedIds);
    selectedIds.forEach((id) => {
      if (!covered.has(id)) orderedIds.push(id);
    });

    onConfirm(orderedIds);
    onClose();
  };

  const getCheckboxState = (task: TaskWithChildren): 'checked' | 'unchecked' | 'indeterminate' => {
    const isSelected = selectedIds.has(task.taskId!);

    if (task.children.length === 0) {
      return isSelected ? 'checked' : 'unchecked';
    }

    const childIds = getChildTaskIds(task);
    const selectedChildCount = childIds.filter((id) => selectedIds.has(id)).length;

    if (isSelected && selectedChildCount === childIds.length) {
      return 'checked';
    } else if (selectedChildCount > 0 || isSelected) {
      return 'indeterminate';
    }
    return 'unchecked';
  };

  const renderTask = (task: TaskWithChildren, depth: number = 0) => {
    const hasChildren = task.children.length > 0;
    const isExpanded = expandedIds.has(task.taskId!);
    const checkboxState = getCheckboxState(task);

    return (
      <div key={task.taskId}>
        <div
          className={`
            flex items-center gap-2 py-2 px-3 rounded-md hover:bg-gray-50 transition-colors
            ${depth > 0 ? 'ml-6' : ''}
          `}>
          {hasChildren ? (
            <button
              type="button"
              onClick={() => handleExpandToggle(task.taskId!)}
              className="p-0.5 text-gray-400 hover:text-gray-600 transition-colors">
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          ) : (
            <div className="w-5" />
          )}

          <Checkbox
            checked={checkboxState === 'checked'}
            indeterminate={checkboxState === 'indeterminate'}
            onChange={() => handleTaskToggle(task)}
          />

          <span
            onClick={() => handleTaskToggle(task)}
            className="text-sm text-gray-700 flex-1 truncate cursor-pointer">
            {task.name}
          </span>

          {task.taskType && (
            <span className="text-xs text-gray-400 px-2 py-0.5 bg-gray-100 rounded">
              {task.taskType}
            </span>
          )}
        </div>

        {hasChildren && isExpanded && (
          <div className="border-l border-gray-200 ml-5">
            {task.children.map((child) => renderTask(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      confirmText={confirmText}
      handleConfirm={handleConfirm}
      size="large"
      disableConfirm={selectedIds.size === 0}>
      <div className="flex flex-col gap-4 h-full">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tasks..."
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <button type="button"
              onClick={handleSelectAll}
              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">
              Select All
            </button>
            <span className="text-gray-300">|</span>
            <button type="button"
              onClick={handleDeselectAll}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium">
              Deselect All
            </button>
          </div>
        </div>

        <div className="text-sm text-gray-500">
          {selectedIds.size} task{selectedIds.size !== 1 ? 's' : ''} selected
        </div>

        <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg">
          {filteredTaskTree.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {searchQuery ? 'No tasks match your search' : 'No tasks available'}
            </div>
          ) : (
            <div className="p-2">
              {filteredTaskTree.map((task) => renderTask(task))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SelectTasksModal;
